import { type Nullable } from '../nullable'
import { type ECommerceProduct } from './products'
import { type DeliveryClass } from './shipping'

export interface WpProductVariation {
  attributes?: Record<string, string>
  display_price?: number
  display_regular_price?: number
  is_in_stock?: boolean
  max_qty?: number | string
  min_qty?: number
  real_sku?: string
  sku?: string
  variation_id?: number
  image?: {
    full_src: string
    srcset: string
  }
}

export interface BundlePrice {
  addon_products: []
  displayed_text: string
  enabled: boolean
  quantity: number
  regular_price: number
  sale_price: number
}

export interface PlanOption {
  automatic_billing_plan: string
  total_quantity: string
  price: string
}

export interface Plan {
  single_delivery_quantity: string
  delivery_frequency: string
  options: PlanOption[]
}

export interface SubscriptionOption {
  selectedPlan?: PlanOption
  selectedBundlePrice?: BundlePrice
  selectedAmount: number
  selectedFrequency: number
}

export interface WpSubshipVariation extends WpProductVariation {
  id: number
  post_id: number
  product_id: number
  site_id: number
  subship_setting_id: number
  real_sku: string
  sku: string
  attributes?: Record<string, string>
  bundle_prices?: BundlePrice[]
  country?: string
  enabled?: boolean
  has_wc_product?: boolean
  plans?: Plan[]
  product_display_name?: string
  product_name?: string
  product_note?: string
  product_price?: number
  product_type?: string
  stock?: number
}

interface SettingOption {
  enabled: boolean
  value: number
}

export interface WpSubshipSetting {
  amount_unit: 'piece' | 'can' | 'box' | 'bag'
  amounts: SettingOption[]
  attributes: string[]
  can_payment_continue: boolean
  display_mode: string
  frequencies: SettingOption[]
  frequency_unit: 'month' | 'week'
  id: number
  name: string
  post_id: number
  site_id: number
  subship_product_id: number
  type: 'subscription' | 'staggered_shipping'
}

export interface WpSubshipSettingDefaultOption {
  // null 代表無推薦方案
  variation_id: number | null
  single_delivery_quantity: string
  delivery_frequency: string
  automatic_billing_plan?: string
  total_quantity?: string
}

export interface ProductDefaultOption {
  variation_id: number
  delivery_frequency: number
  deliver_quantity: number
  billing_plan: number
}

export interface VariationSubscription {
  variation_id: number
  subscribed: boolean
  following: boolean
}

export interface MyAccountSidebarDisplay {
  /** 點數/好友推薦 */
  point: boolean
  /** 點數/好友推薦 */
  referral_program: boolean
  /** 我的訂單 */
  service_order: boolean
  /** 帳號設定 */
  password_setting: boolean
  /** 點數/好友推薦 */
  recipient_address: boolean
  /** 追蹤清單 */
  product_subscription: boolean
}

export interface WpBanner {
  id: number
  index: number
  image_url: string
  link_url: string
}

export interface WpOrderActionSetting {
  key?: string
  wp_nonce?: string
}

export enum OrderAction {
  CANCEL = 'cancel',
  REPEAT = 'repeat',
  PAY = 'pay',
}

export interface ProductSubscriptionStatus {
  parent_product_id: string
  variation_subscriptions: {
    variation_id: string | number
    following: boolean
    subscribed: boolean
  }[]
}

export type ProductVariationAttributes = Record<string, string>
export interface ProductVariationDimensions {
  length: string
  width: string
  height: string
}
export interface ProductVariationImage {
  title: string
  caption: string
  url: string
  alt: string
  src: string
  srcset: string
  sizes: string
  full_src: string
  full_src_w: number
  full_src_h: number
  gallery_thumbnail_src: string
  gallery_thumbnail_src_w: number
  gallery_thumbnail_src_h: number
  thumb_src: string
  thumb_src_w: number
  thumb_src_h: number
  src_w: number
  src_h: number
  src_webp: string
  full_src_webp: string
  gallery_thumbnail_src_webp: string
  thumb_src_webp: string
  srcset_webp: string
}
export interface MuProductVariation {
  /**
   * 唯一識別碼，拿來給 ec 的 api 在購物車取得商品資訊
   */
  id: ECommerceProduct['id']
  delivery_class: DeliveryClass
  attributes: ProductVariationAttributes
  availability_html: string
  backorders_allowed: boolean
  dimensions: ProductVariationDimensions
  dimensions_html: string
  display_price: number
  display_regular_price: number
  image: ProductVariationImage
  image_id: number
  is_downloadable: boolean
  is_in_stock: boolean
  is_purchasable: boolean
  is_sold_individually: string
  is_virtual: boolean
  max_qty: number
  min_qty: number
  price_html: string
  sku: string
  variation_description: string
  variation_id: number | string
  variation_is_active: boolean
  variation_is_visible: boolean
  weight: string
  weight_html: string
  real_sku: string
  is_pre_ordered: boolean
  pre_ordered_item_description: string | null
  introduction: string
  product_limited?: Nullable<{
    life_limit: number
    purchased_count: number
  }>
}
export interface ProductGalleryImage {
  title: string
  caption: string
  url: string
  alt: string
  src: string
  srcset: string
  sizes: string
  full_src: string
  full_src_w: number
  full_src_h: number
  gallery_thumbnail_src: string
  gallery_thumbnail_src_w: number
  gallery_thumbnail_src_h: number
  thumb_src: string
  thumb_src_w: number
  thumb_src_h: number
  src_w: number
  src_h: number
}
